import { STATE_TO_IANA_TZ } from '@bonsai/shared';

import type { RegionNode } from '../../api/regions';
import { flattenById, subtreeIds } from '../../utils/regionTree';
import type { VenueRosterRow } from '../dashboards/venues/roster/venues-roster-api';

export type VenueRow = Pick<VenueRosterRow, 'id' | 'name' | 'state' | 'timeZone' | 'region'>;

export interface VenueOption {
  id: string;
  name: string;
  /** The venue's own zone, else its state's, else null. */
  timeZone: string | null;
}

/** The active venues inside `regionId` and everything beneath it, by name. */
export function toVenueOptions(
  rows: VenueRow[],
  tree: RegionNode[],
  regionId: string,
): VenueOption[] {
  if (!regionId) return [];
  const inScope = subtreeIds(flattenById(tree), regionId);
  return rows
    .filter((v) => v.region !== null && inScope.has(v.region.id))
    .map((v) => ({
      id: v.id,
      name: v.name,
      timeZone: v.timeZone ?? STATE_TO_IANA_TZ[v.state] ?? null,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function venueInRegion(options: VenueOption[], venueId: string): boolean {
  return venueId !== '' && options.some((v) => v.id === venueId);
}

/** The most common zone among the venues; a tie goes to the alphabetically
 *  first zone so the default never flips between renders. */
export function mostCommonZone(options: Pick<VenueOption, 'timeZone'>[]): string {
  const counts = new Map<string, number>();
  for (const { timeZone } of options) {
    if (timeZone) counts.set(timeZone, (counts.get(timeZone) ?? 0) + 1);
  }
  const ranked = [...counts].sort(([za, ca], [zb, cb]) => cb - ca || za.localeCompare(zb));
  return ranked[0]?.[0] ?? '';
}

export function regionDefaultZone(rows: VenueRow[], tree: RegionNode[], regionId: string): string {
  return mostCommonZone(toVenueOptions(rows, tree, regionId));
}
